import type { SearchClass } from '@Types/Search/Search';
import type {
	ActionMenuElementDecorations,
	LogoClassType,
	MenuClassType,
	NavContent,
	NavElements,
	valueType,
} from '@Types/Shared/Header';
import type { Options } from '@Types/Shared/Options';
import type { MenusType, SiteType } from '@Types/Shared/SiteDatas';

import { Fragment, type ReactElement } from 'react';

import { cn } from '@Blocks/_components/ui/utils';

import CtaMenu from './CtaMenu';
import Langs from './Langs';
import Logo from './Logo';
import Menu from './Menu';
import Search from './Search';

type NavType = {
	nav: NavContent;
	site: SiteType;
	menus: Partial<MenusType>;
	classVariant: string;
	navType: string;
	ariaAbelledby: string;
	minosOptions: Options;
	actionIconsStyle?: ActionMenuElementDecorations;
};

export default function NavCard(props: NavType) {
	const { nav, site, menus, classVariant, navType, ariaAbelledby, minosOptions, actionIconsStyle } =
		props;

	const elements: NavElements = nav.elements;
	let menuClass: MenuClassType = {};
	let logoClass: LogoClassType = {};
	let searchClass: SearchClass = {};
	const variantMenuChildItems = nav.types.variantMenuChildItems;
	const caretIcon = nav.types.caretIcon;

	if (navType === 'topNav') {
		menuClass = {
			wrapper: 'py-4',
			menuItems:
				'minos-header-topnav-menuItems text-base tablet:minos-header-topnav-menuItems hover:text-minos-header-topnav-parent-element-hover-color focus:text-minos-header-topnav-parent-element-hover-color',
			childMenuItems:
				'minos-header-topnav-childMenuItems text-base tablet:minos-header-topnav-childMenuItems hover:text-minos-header-topnav-child-element-hover-color focus:text-minos-header-topnav-child-element-hover-color',
			menuChildBg: 'bg-minos-header-topnav-menuchild-bg',
			menuChildRadius: 'rounded-minos-header-topnav-menuchild-radius',
			menuChildBorder: 'minos-header-topnav-menuchild-border',
			iconColor: 'text-minos-header-topnav-icon-color',
			childSeparationColor: 'before:bg-minos-header-topnav-icon-color',
			menuChildSvg: 'fill-minos-header-topnav-menuchild-bg',
		};
		logoClass = {
			logo: 'minos-header-topnav-logo-size-width minos-header-topnav-logo-size-height',
			wrapper: 'py-4',
		};
		searchClass = {
			wrapper: 'py-4',
			search: 'minos-header-topnav-search ',
			searchBg: 'bg-minos-header-topnav-search-bg',
			searchRadius: 'rounded-minos-header-topnav-search-radius',
			iconColor:
				'text-minos-header-topnav-icon-color hover:text-minos-header-topnav-parent-element-hover-color group-focus:text-minos-header-topnav-parent-element-hover-color',
			border: 'minos-header-topnav-search-border',
		};
	} else {
		menuClass = {
			wrapper: 'py-5',
			menuItems:
				'minos-header-mainnav-menuItems text-base tablet:minos-header-mainnav-menuItems hover:text-minos-header-mainnav-parent-element-hover-color focus:text-minos-header-mainnav-parent-element-hover-color',
			childMenuItems:
				'minos-header-mainnav-childMenuItems text-base tablet:minos-header-mainnav-childMenuItems hover:text-minos-header-mainnav-child-element-hover-color focus:text-minos-header-mainnav-child-element-hover-color',
			menuChildBg: 'bg-minos-header-mainnav-menuchild-bg',
			menuChildRadius:
				'rounded-minos-header-mainnav-menuchild-radius border-minos-header-mainnav-menuchild-border-color',
			menuChildBorder: 'minos-header-mainnav-menuchild-border',
			iconColor: 'text-minos-header-mainnav-icon-color',
			childSeparationColor: 'before:bg-minos-header-mainnav-icon-color',
			menuChildSvg: 'fill-minos-header-mainnav-menuchild-bg',
		};
		logoClass = {
			logo: 'minos-header-mainnav-logo-size-width minos-header-mainnav-logo-size-height',
			wrapper: 'py-5',
		};
		searchClass = {
			wrapper: 'py-5',
			search: 'minos-header-mainnav-search',
			searchBg: 'bg-minos-header-mainnav-search-bg',
			searchRadius: 'rounded-minos-header-mainnav-search-radius',
			iconColor:
				'text-minos-header-mainnav-icon-color hover:text-minos-header-mainnav-parent-element-hover-color group-focus:text-minos-header-mainnav-parent-element-hover-color',
			border: 'minos-header-mainnav-search-border',
		};
	}

	type ContentType = {
		custom_logo?: ReactElement;
		secondary_logo?: ReactElement;
		primary_menu?: ReactElement;
		secondary_menu?: ReactElement;
		cta_menu?: ReactElement;
		lang_menu?: ReactElement;
		search?: ReactElement;
	};

	const contentType: ContentType = {
		custom_logo: (
			<Logo site={site} logo={site.custom_logo} itemClass={logoClass} position={elements} />
		),
		secondary_logo: (
			<Logo site={site} logo={site.secondary_logo} itemClass={logoClass} position={elements} />
		),
		primary_menu: (
			<Menu
				menu={menus.primary_menu}
				itemClass={menuClass}
				variantMenuChildItems={variantMenuChildItems}
				caretIcon={caretIcon}
				addSeparation={false}
			/>
		),
		secondary_menu: (
			<Menu
				menu={menus.secondary_menu}
				itemClass={menuClass}
				variantMenuChildItems={variantMenuChildItems}
				caretIcon={caretIcon}
				addSeparation={false}
			/>
		),
		cta_menu: (
			<CtaMenu
				navType={navType}
				CTAMenu={menus.cta_menu}
				minosOptions={minosOptions}
				btnStyle={nav.types.cta ? nav.types.cta : ''}
			/>
		),
		lang_menu: (
			<Langs langs={site.langs} itemClass={menuClass} variantMenuChildItems={variantMenuChildItems} />
		),
		search: <Search homeUrl={site.url} itemClass={searchClass} actionIconsStyle={actionIconsStyle} />,
	};

	return (
		<nav
			aria-labelledby={ariaAbelledby}
			className={cn('col-start-2 col-end-14 flex items-center gap-2 relative', classVariant)}
		>
			<div className='flex grow items-center justify-start gap-10'>
				{elements.left && elements.left.length > 0 && (
					<>
						{elements.left.map((item: valueType) => (
							<Fragment key={item.id}>{contentType[item.value]}</Fragment>
						))}
					</>
				)}
			</div>

			<div className='flex grow items-center justify-center gap-10'>
				{elements.center && elements.center.length > 0 && (
					<>
						{elements.center.map((item: valueType) => (
							<Fragment key={item.id}>{contentType[item.value]}</Fragment>
						))}
					</>
				)}
			</div>

			<div className='flex grow items-center justify-end gap-10'>
				{elements.right && elements.right.length > 0 && (
					<>
						{elements.right.map((item: valueType) => (
							<Fragment key={item.id}>{contentType[item.value]}</Fragment>
						))}
					</>
				)}
			</div>
		</nav>
	);
}
